import { useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  useClientByIdQuery,
  usePaymentMethodQuery,
  useSpecialPricingRuleBrandQuery,
  useSpecialPricingRuleCreateMutation,
  useSpecialPricingRuleUpdateMutation,
  useSupplierQuery,
} from 'api';
import { Form, FormProps, notification } from 'components';
import { ISpecialPricingRulesAddUpdateModal } from 'feature/clientTabs/payments/specialPricingRules/addEdit/index';
import { ErrorMappingValueType } from 'typings';
import { SpecialPricingRuleFieldType } from 'typings/form/client/payment';

export const useSpecialPricingRulesAddEdit = (props: ISpecialPricingRulesAddUpdateModal) => {
  const { dataToEdit, onModalStateChange, modalState } = props;
  const { id } = useParams() as { id: string };
  const [form] = Form.useForm();
  const [notificationApi, contextHolder] = notification.useNotification();
  const { data: clientDetails = {} } = useClientByIdQuery({ id });
  const { data: pricingRuleBrandData = [] } = useSpecialPricingRuleBrandQuery({ id });
  const { data: paymentMethodData = [] } = usePaymentMethodQuery();
  const { data = {} } = useSupplierQuery({ page: 1, size: 50 });
  const { content: supplierData = [] } = data;

  const [createSpecialPricingRule, { isLoading: createIsLoading }] = useSpecialPricingRuleCreateMutation();
  const [updateSpecialPricingRule, { isLoading: updateIsLoading }] = useSpecialPricingRuleUpdateMutation();

  const formattedSupplierData = useMemo(() => {
    return [...supplierData, { label: 'Default for the Brand', value: 'default' }];
  }, [supplierData]);
  const formattedPricingRuleBrandData = useMemo(() => {
    const data = structuredClone(pricingRuleBrandData);
    if (dataToEdit) {
      data.push(dataToEdit.brand);
    }
    return data;
  }, [dataToEdit, pricingRuleBrandData]);

  const openNotificationWithIcon = useCallback(
    (messageContent: ErrorMappingValueType) => {
      const { type, message } = messageContent;
      notificationApi[type]({
        message: message,
      });
    },
    [notificationApi]
  );

  const onCancel = useCallback(() => {
    form.resetFields();
    onModalStateChange();
  }, [form, onModalStateChange]);

  const onFinish: FormProps<SpecialPricingRuleFieldType>['onFinish'] = useCallback(
    (values: SpecialPricingRuleFieldType) => {
      const payload = values;
      if (dataToEdit) {
        payload.id = dataToEdit.id;
      } else {
        payload.clientId = id as string;
      }
      if (payload.mainSupplierId === 'default') {
        payload.defaultSupplier = true;
        payload.mainSupplierId = null;
      }
      if (payload.pricingReferenceSupplierId === 'default') {
        payload.defaultPricingReferenceSupplier = true;
        payload.pricingReferenceSupplierId = null;
      }
      (dataToEdit ? updateSpecialPricingRule(payload) : createSpecialPricingRule(payload))
        .then(({ data, error }: any) => {
          if (data?.success) {
            openNotificationWithIcon({
              type: 'success',
              message: `Special Pricing Rule was successfully ${dataToEdit ? 'updated' : 'created'}.`,
            });
            onCancel();
          } else if (!error.data.success) {
            const { data = {} } = error.data;
            Object.keys(data).forEach((_key: string) => {
              openNotificationWithIcon({
                type: 'error',
                message: data[_key],
              });
            });
          } else if (error.data.error) {
            openNotificationWithIcon({
              type: 'error',
              message: 'Something went wrong!',
            });
          }
        })
        .catch((err) => {
          console.log(err, 'error');
        });
    },
    [id, createSpecialPricingRule, openNotificationWithIcon, updateSpecialPricingRule, dataToEdit, onCancel]
  );

  useEffect(() => {
    if (dataToEdit && modalState) {
      form.setFieldsValue({
        ...dataToEdit,
        brandId: dataToEdit.brand?.id,
        mainSupplierId: dataToEdit.defaultSupplier ? 'default' : dataToEdit.mainSupplier?.id,
        pricingReferenceSupplierId: dataToEdit.defaultPricingReferenceSupplier
          ? 'default'
          : dataToEdit.pricingReferenceSupplier?.id,
      });
    }
  }, [dataToEdit, form, modalState]);
  return {
    onFinish,
    onCancel,
    form,
    contextHolder,
    paymentMethodData,
    clientDetails,
    formattedSupplierData,
    formattedPricingRuleBrandData,
    isLoading: updateIsLoading || createIsLoading,
  };
};
